import RoleGuard from "@/pages/role-guard";
import UserDetails from "@/pages/users/details";
import ExternalUserDetails from "@/pages/users/external/details";
import NewExternalUser from "@/pages/users/external/new";
import { EditApprovalGroup } from "@/pages/users/groups/edit";
import { CreateApprovalGroup } from "@/pages/users/groups/new";
import NewUser from "@/pages/users/new";
import { createApprovalGroupDetailsBreadcrumb } from "@/queries/approval-groups";
import { createExternalUserDetailsBreadcrumb } from "@/queries/users/external-users.api";
import { createUserDetailsBreadcrumb } from "@/queries/users/users.api";
import { Permissions } from "@/utils/permissions";
import type { QueryClient } from "@tanstack/react-query";
import { lazy } from "react";
import {
  makeHandle,
  withNotFoundRedirect,
  type LoaderParams,
} from "./router.helpers";

const UsersAndGroups = lazy(() =>
  import("@/pages/users/UsersAndGroups").then((m) => ({
    default: m.UsersAndGroups,
  })),
);

export const createUsuariosRoutes = (queryClient: QueryClient) => ({
  path: "usuarios",
  children: [
    {
      index: true,
      Component: () => (
        <RoleGuard
          permissions={[
            Permissions.users.read,
            Permissions["approval-groups"].read,
          ]}
          canHaveSomePermissions
        >
          <UsersAndGroups />
        </RoleGuard>
      ),
      handle: makeHandle({ label: "Usuarios" }),
    },
    {
      path: "grupos/new",
      element: (
        <RoleGuard permissions={[Permissions["approval-groups"].create]}>
          <CreateApprovalGroup />
        </RoleGuard>
      ),
      handle: makeHandle(
        { label: "Grupos", path: "/usuarios?tab=groups" },
        { label: "Crear grupo" },
      ),
    },
    {
      path: "grupos/:id",
      element: (
        <RoleGuard permissions={[Permissions["approval-groups"].update]}>
          <EditApprovalGroup />
        </RoleGuard>
      ),
      loader: ({ params }: LoaderParams) =>
        withNotFoundRedirect(
          () =>
            createApprovalGroupDetailsBreadcrumb(
              queryClient,
              params.id || "",
            )(),
          "/usuarios?tab=groups",
        ),
      handle: {
        breadcrumb: (match: { loaderData?: { data?: { name?: string } } }) => [
          { label: "Grupos", path: "/usuarios?tab=groups" },
          { label: match?.loaderData?.data?.name ?? "Detalle del Grupo" },
        ],
      },
    },
    {
      path: "new",
      element: (
        <RoleGuard permissions={[Permissions.users.create]}>
          <NewUser />
        </RoleGuard>
      ),
      handle: makeHandle(
        { label: "Usuarios", path: "/usuarios" },
        { label: "Crear usuario interno" },
      ),
    },
    {
      path: "externos/new",
      element: (
        <RoleGuard permissions={[Permissions.users.create]}>
          <NewExternalUser />
        </RoleGuard>
      ),
      handle: makeHandle(
        { label: "Usuarios", path: "/usuarios?user-tab=externos" },
        { label: "Crear usuario externo" },
      ),
    },
    {
      path: "externos/:id",
      element: (
        <RoleGuard permissions={[Permissions.users.update]}>
          <ExternalUserDetails />
        </RoleGuard>
      ),
      loader: ({ params }: LoaderParams) =>
        withNotFoundRedirect(
          () =>
            createExternalUserDetailsBreadcrumb(queryClient, params.id || "")(),
          "/usuarios?user-tab=externos",
        ),
      handle: {
        breadcrumb: (match: { loaderData?: { data?: { name?: string } } }) => [
          {
            label: "Usuarios",
            path: "/usuarios?user-tab=externos",
          },
          {
            label: match?.loaderData?.data?.name || "Detalles del usuario", // NOSONAR
          },
        ],
      },
    },
    {
      path: ":id",
      element: (
        <RoleGuard permissions={[Permissions.users.update]}>
          <UserDetails />
        </RoleGuard>
      ),
      loader: ({ params }: LoaderParams) =>
        withNotFoundRedirect(
          () => createUserDetailsBreadcrumb(queryClient, params.id || "")(),
          "/usuarios",
        ),
      handle: {
        breadcrumb: (match: { loaderData?: { data?: { name?: string } } }) => [
          { label: "Usuarios", path: "/usuarios" },
          { label: match?.loaderData?.data?.name || "Detalles del usuario" },
        ],
      },
    },
  ],
});
